/* eslint-disable no-console */
import type { ReactNode } from 'react';
import { useEffect, useMemo } from 'react';

import { CLIENT_HEADERS, devEnv, isProduction } from '#src/config.ts';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';

import { AnalyticsContext } from './index.ts';
import {
  getSessionReplayProperties,
  shutdownSessionReplay,
} from './sessionReplayModule.ts';
import { sessionReplayState } from './sessionReplayState.ts';
import { maybeStartSessionReplay } from './startSessionReplay.ts';

type Props = {
  children: ReactNode;
};

declare global {
  interface Window {
    gtag: (
      type: string,
      event: string,
      options: Record<string, unknown>
    ) => void | undefined;
    analytics: {
      ready: (fn: () => void) => void;
      identify: (userId: string, traits?: Record<string, unknown>) => void;
      track: (
        eventName: string,
        properties?: Record<string, unknown>,
        options?: Record<string, unknown>
      ) => void;
      reset: () => void;
      user: () => {
        id: () => string;
        traits: (traits?: Record<string, unknown>) => Record<string, unknown>;
        anonymousId: () => string;
      };
      // From https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#page
      // > "if you pass only one string to page it is assumed to be name"
      page: (pageName: string | undefined) => void;
      addSourceMiddleware: (
        middleware: (options: {
          payload: Record<string, unknown>;
          next: (payload: Record<string, unknown>) => unknown;
          integrations: Record<string, unknown>;
        }) => void
      ) => void;
      load: () => void;
    };
  }
}

export const ETH_DEPOSITED_TRAIT = 'eth_deposited';
const ETH_WITHDRAWN_TRAIT = 'eth_withdrawn';
const ETH_TRANSFERRED_TRAIT = 'eth_transferred';

// By default segment analytics.js caches the user traits and sends them
// with each identify call. As some of amplitude traits have special
// properties like increment (trait is incremented by the sent value
// on each identify call).
const TRAITS_TO_RESET = [
  ETH_DEPOSITED_TRAIT,
  ETH_WITHDRAWN_TRAIT,
  ETH_TRANSFERRED_TRAIT,
];

const resetUserTraits = () => {
  if (!window.analytics) return;

  const { user } = window.analytics;
  if (user) {
    const traits = user().traits();
    TRAITS_TO_RESET.forEach(t => delete traits[t]);
    user().traits(traits);
  }
};

type IdentifyArgs = {
  userId?: string;
  traits?: Record<string, unknown>;
};

const identify = ({ userId, traits }: IdentifyArgs): void => {
  if (!window.analytics) return;

  const { user } = window.analytics;
  if (!userId && !user) {
    console.warn('Unable to identify without a userId');
    return;
  }

  const uuid = userId || user().id();

  window.analytics.identify(uuid, traits);
  if ((!isProduction || devEnv) && import.meta.env.MODE !== 'test') {
    console.log('🪲 Analytics User Identified: ', uuid, traits);
  }

  resetUserTraits();
};

export const EventsProvider = ({ children }: Props) => {
  const { isIosApp, isAndroidApp, isFlutterApp, postMessage } =
    useIsMobileApp();
  const {
    flags: { sessionReplayEnabled = false },
  } = useFeatureFlags();

  useEffect(
    () => () => {
      shutdownSessionReplay();
      sessionReplayState.active = false;
    },
    []
  );

  const value = useMemo(() => {
    const requestClient = CLIENT_HEADERS['sorare-client'];
    const requestClientProperties = {
      request_client: requestClient,
      request_client_context: isIosApp
        ? 'ios'
        : isAndroidApp
          ? 'android'
          : requestClient,
      request_client_version: CLIENT_HEADERS['sorare-version'],
      request_client_build: CLIENT_HEADERS['sorare-build'],
    };
    return {
      identify: (id: string, traits?: Record<string, unknown>) =>
        identify({ userId: id, traits }),
      track: (event: string, properties?: Record<string, unknown>): void => {
        maybeStartSessionReplay(event, sessionReplayEnabled);

        const enrichedProperties = {
          ...requestClientProperties,
          viewport_width: window.innerWidth,
          viewport_height: window.innerHeight,
          ...properties,
        };

        if (window.analytics) {
          window.analytics.track(event, {
            ...getSessionReplayProperties(),
            ...enrichedProperties,
          });
        }

        if (isFlutterApp) {
          postMessage('trackInAppEvent', {
            event,
            jsonProperties: JSON.stringify(enrichedProperties),
          });
        }

        if ((!isProduction || devEnv) && import.meta.env.MODE !== 'test') {
          console.log(
            '🪲 Analytics Event Tracked: "%s"',
            event,
            enrichedProperties
          );
        }
      },
    };
  }, [isIosApp, isAndroidApp, isFlutterApp, postMessage, sessionReplayEnabled]);

  return <AnalyticsContext value={value}>{children}</AnalyticsContext>;
};
